<template>
  <v-row id="projectsheet" class="pb-6">
    <v-col v-for="(col, colIndex) in blocksCols" :key="colIndex" :cols="12 / blocksCols.length">
      <div class="d-flex flex-column gap-4">
        <template v-for="(block, blockIndex) in col" :key="blockIndex">
          <template v-if="block == 'header'">
            <div class="break-inside-avoid">
              <div class="d-flex align-center" style="min-height: 40px">
                <b>{{ $t('PROJECT_SHEET.HEADER') }}</b>
                <v-spacer></v-spacer>
                <premium-section feature="projectsheet-moveblocks">
                  <v-btn-icon size="small" class="export-hidden">
                    <v-icon>far fa-arrow-down-big-small</v-icon>
                    <v-menu location="bottom" activator="parent" :close-on-content-click="false">
                      <v-list density="comfortable">
                        <draggable :list="blocksState" item-key="id" @update="updateBlocks">
                          <template #item="{ element }">
                            <v-list-item :key="element.id" class="grab" :class="{ 'text-disabled': ! element.visible }">
                              <template #prepend>
                                <div class="d-flex align-center mr-2">
                                  <v-icon size="small" class="mr-1" style="opacity: 0.7">far fa-grip-vertical</v-icon>
                                  <v-checkbox-btn v-model="element.visible" density="compact" :disabled="element.id == 'header'" @update:model-value="updateBlocks">
                                  </v-checkbox-btn>
                                </div>
                              </template>
                              {{ $t(element.labelKey) }}
                            </v-list-item>
                          </template>
                        </draggable>
                        <v-btn-icon size="x-small" style="position: absolute; top: 4px; right: 4px" @click="resetBlocks()">
                          <v-icon>far fa-undo</v-icon>
                        </v-btn-icon>
                      </v-list>
                    </v-menu>
                  </v-btn-icon>
                  <template #noaccess>
                    <div :title="$t('PREMIUM.AVAILABLE_IN_PACK', { pack: 'BUSINESS' })">
                      <v-btn-icon disabled size="small" class="export-hidden">
                        <v-icon>far fa-cog</v-icon>
                      </v-btn-icon>
                    </div>
                  </template>
                </premium-section>
                <premium-section feature="projectsheet-export">
                  <v-btn-icon :disabled="exporting.inprogress" size="small" class="export-hidden" @click="! exporting.inprogress && pdfExport()">
                    <v-icon>{{ exporting.inprogress ? 'far fa-spinner fa-spin' : 'far fa-file-pdf' }}</v-icon>
                  </v-btn-icon>
                  <template #noaccess>
                    <div :title="$t('PREMIUM.AVAILABLE_IN_PACK', { pack: 'TEAM' })">
                      <v-btn-icon disabled size="small" class="export-hidden">
                        <v-icon>far fa-file-pdf</v-icon>
                      </v-btn-icon>
                    </div>
                  </template>
                </premium-section>
              </div>
              <project-sheet-header :planning="planning" :close-editing="exporting.closeEditing"></project-sheet-header>
            </div>

            <client-custom-component v-if="headerCustomField" :name="headerCustomField.component" :planning="planning" :close-editing="exporting.closeEditing"
                                     @update="$emit('update', $event)">
            </client-custom-component>
          </template>

          <template v-else-if="block == 'targets'">
            <project-sheet-targets :planning="planning" :close-editing="exporting.closeEditing" class="h-100 d-flex flex-column break-inside-avoid"
                                   @update="$emit('update', $event)">
            </project-sheet-targets>
          </template>

          <template v-else-if="block == 'waypoints'">
            <project-sheet-waypoints :planning="planning" class="break-inside-avoid" :close-editing="exporting.closeEditing"
                                     @update="$emit('update', $event)">
            </project-sheet-waypoints>
            <client-custom-component v-if="waypointsCustomField" :name="waypointsCustomField.component" :planning="planning"></client-custom-component>
          </template>

          <template v-else-if="block == 'activity'">
            <project-sheet-activity :planning="planning" class="h-100 d-flex flex-column break-inside-avoid">
            </project-sheet-activity>
          </template>

          <template v-else-if="block == 'description'">
            <project-sheet-description :planning="planning" :close-editing="exporting.closeEditing" class="break-inside-avoid"
                                       @update="$emit('update', $event)">
            </project-sheet-description>
          </template>

          <template v-else-if="block == 'users'">
            <project-sheet-users :planning="planning" class="break-inside-avoid"
                                 @open-project-report="$emit('open-project-report', $event)" @reload-planning="$emit('reload-planning')">
            </project-sheet-users>
          </template>

          <template v-else-if="block == 'budget'">
            <project-sheet-budget-summary :planning="planning" :no-animation="exporting.noanimation" class="break-inside-avoid"
                                          @open-project-report="$emit('open-project-report', $event)">
            </project-sheet-budget-summary>
          </template>

          <template v-else-if="block == 'links'">
            <project-sheet-links :planning="planning" :close-editing="exporting.closeEditing" class="break-inside-avoid"
                                 @open-project-report="$emit('open-project-report', $event)" @update="$emit('update', $event)">
            </project-sheet-links>
          </template>
        </template>
      </div>
    </v-col>
  </v-row>
</template>

<style>
  #projectsheet .v-card {
    min-height: 190px;
    align-content: center;
  }
</style>

<script>
  import draggable from 'vuedraggable';
  import clientConfig from '@/client_customs/config';
  import darkModeMixin from '@/components/Manage/ManageProfile/darkModeMixin';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import ProjectSheetHeader from './ProjectSheetHeader.vue';
  import ProjectSheetDescription from './ProjectSheetDescription.vue';
  import ProjectSheetTargets from './ProjectSheetTargets.vue';
  import ProjectSheetWaypoints from './ProjectSheetWaypoints.vue';
  import ProjectSheetLinks from './ProjectSheetLinks.vue';
  import ProjectSheetUsers from './ProjectSheetUsers.vue';
  import ProjectSheetBudgetSummary from './ProjectSheetBudgetSummary.vue';
  import ProjectSheetActivity from './ProjectSheetActivity.vue';

  export default {
    components: {
      draggable,
      ProjectSheetHeader,
      ProjectSheetBudgetSummary,
      ProjectSheetDescription,
      ProjectSheetTargets,
      ProjectSheetWaypoints,
      ProjectSheetLinks,
      ProjectSheetUsers,
      ProjectSheetActivity,
    },
    mixins: [darkModeMixin],
    props: {
      planning: { type: Object, required: true },
    },
    emits: ['open-project-report', 'update', 'reload-planning'],
    data() {
      return {
        blocksState: [],
        exporting: { inprogress: false, success: false, error: false, screenshoting: false, cols: null, closeEditing: false, noanimation: false },
        headerCustomField: clientConfig.projectsheet?.headerCustomField || null,
        waypointsCustomField: clientConfig.projectsheet?.waypointsCustomField || null,
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      blocksCols() {
        const blocks = this.blocksState.filter(block => block.visible || block.id == 'header').map(block => block.id);
        let cols = this.$vuetify.display.smAndDown ? 1 : this.$vuetify.display.mdAndDown ? 2 : 3;
        if (this.exporting.cols) cols = this.exporting.cols; // force 2 columns for export
        const blocksCols = Array.from({ length: cols }, () => []);
        blocks.forEach((block, index) => blocksCols[index % cols].push(block));
        return blocksCols;
      },
      safeProjectName() {
        return this.planning.getTitle().replace(/<(?:.|\n)*?>/gm, ' ').replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&gt;/g, ">").replace(/&lt;/g, "<").replace(/\//g, ' ');
      },
      isPremium() { return this.usersAccessRightStore.isPremium; },
    },
    watch: {
      'planning.projectsheet.blocks': {
        handler(blocks) {
          this.buildBlocksState(blocks);
        },
        immediate: true,
      },
    },
    methods: {
      buildBlocksState(blocks) {
        const availableBlocks = ['header', 'targets', 'budget', 'waypoints', 'description', 'links', 'activity', 'users'];
        const idsList = blocks?.length ? blocks : availableBlocks;
        this.blocksState = idsList.map((blockId) => {
          return { id: blockId, labelKey: `PROJECT_SHEET.${blockId.toUpperCase()}`, visible: true };
        }).concat(_.difference(availableBlocks, idsList).map((blockId) => {
          return { id: blockId, labelKey: `PROJECT_SHEET.${blockId.toUpperCase()}`, visible: false };
        }));
      },
      updateBlocks() {
        this.$emit('update', { blocks: this.blocksState.filter(block => block.visible).map(block => block.id) });
      },
      resetBlocks() {
        this.buildBlocksState();
        this.updateBlocks();
      },
      async pdfExport() {
        const mask = document.createElement('div');
        mask.style = "position: fixed; inset: 0; z-index: 9999;";
        mask.style.backgroundColor = this.darkMode ? '#232323' : '#ffffff';
        document.body.appendChild(mask);

        _.extend(this.exporting, { inprogress: true, success: false, error: false, cols: 2, closeEditing: true, noanimation: true });
        const darkModeValue = this.darkMode;
        this.darkMode = false; // force light mode for export
        await new Promise((resolve) => { setTimeout(resolve, 200); }); // wait for the DOM to update

        const $html = document.querySelector('html').cloneNode(true);

        // Clone the canvas
        const originalCanvas = document.querySelectorAll('canvas');
        $html.querySelectorAll("canvas").forEach((el, index) => {
          const image = new Image();
          image.src = originalCanvas[index].toDataURL("image/png");
          image.style = originalCanvas[index].style.cssText;
          image.style.width = '100%';
          el.outerHTML = image.outerHTML;
        });

        this.exporting.cols = null;
        // do not restore animation to avoid recreating chart
        this.darkMode = darkModeValue; // restore dark mode
        setTimeout(() => { mask.parentNode.removeChild(mask); }, 200); // wait for the DOM to update then remove mask

        const queryEl = $html.querySelector('#projectsheet') || document.createElement('div');
        $html.querySelector('body').innerHTML = `<div class="v-application mx-4"><div class="v-row pa-4" style="max-width: 860px">${queryEl.innerHTML}</div></div>`;
        $html.querySelector('body').classList.add('bg-background');
        $html.querySelectorAll(".export-hidden").forEach((el) => { el.parentNode.removeChild(el); });
        $html.querySelectorAll(".v-card").forEach(el => el.style.setProperty('break-inside', 'avoid'));

        window.apiSrv.call('pdf', 'store', { html: $html.innerHTML, orientation: "portrait", footer: `${this.$t('PROJECT_SHEET.PROJECT_SHEET')} - ${this.planning.getTitle()}` }).then((response) => {
          if (response && response.data && response.data.pdfurl) {
            this.exporting.inprogress = false;
            this.exporting.success = true;
            setTimeout(() => { this.exporting.success = false; }, 3000);
            window.open(`${response.data.pdfurl}/${this.safeProjectName} - ${this.$t('PROJECT_SHEET.PROJECT_SHEET')}.pdf`, "_blank");
          }
        }).catch((message) => {
          this.exporting.inprogress = false;
          this.exporting.error = message || "Error : not exported";
        });
      },
    },
  };
</script>
